import { zodResolver } from "@hookform/resolvers/zod";
import { isWeb, type Property, useValidateAddressInLocalizationMutation } from "@stepapp/core";
import {
  Block,
  BottomSheet,
  Button,
  Divider,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import PresenceInfoQuestion from "../../components/Service/PresenceInfoQuestion";
import { useCreateOrUpdateAddress } from "../../hooks";
import { useAppSelector } from "../../hooks/redux";
import { useAddressFormSchema } from "../../schemas";
import type { ReservationFormParams } from "../../store/draftReservation/types";
import AddressForm from "../Service/AddressForm";
import HomeTypeQuestion from "../Service/HomeTypeQuestion";
import PropertyItem from "../Service/PropertyItem";
import LocationDisclaimerSheet from "./LocationDisclaimerSheet";

type Props = {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  currentPropertyId: string;
  properties: Property[];
  setNewPropertyId: Dispatch<SetStateAction<string>>;
};

const SelectAddressSheet = ({
  visible,
  setVisible,
  properties,
  currentPropertyId,
  setNewPropertyId,
}: Props) => {
  const { t } = useTranslation();
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const { md } = useBreakpoints();
  const [propertyId, setPropertyId] = useState(currentPropertyId);
  const [loading, setLoading] = useState(false);
  const [addNewAddress, setAddNewAddress] = useState(false);
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);
  const [createOrUpdateAddress] = useCreateOrUpdateAddress({
    localizationId,
    isUserLogged: true,
  });
  const [validateAddress] = useValidateAddressInLocalizationMutation();

  const addressSchema = useAddressFormSchema();

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ReservationFormParams>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(addressSchema) as unknown as Resolver<ReservationFormParams>,
    defaultValues: {
      address: {
        city: "",
        streetAddress1: "",
        streetAddress2: "",
        doorCode: "",
        postalOrZipCode: "",
        notes: "",
        floorArea: 0,
      },
      meta: {
        keyInfo: "",
        willCustomerBePresent: "deselected" as const,
        homeTypePlaceholder: "",
      },
    },
  });

  const [address, meta] = watch(["address", "meta"]);

  const handleSelectProperty = useCallback(
    (id: string) => {
      setPropertyId(propertyId === id ? "" : id);
    },
    [propertyId],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setAddNewAddress(false);
    setPropertyId(currentPropertyId);
  }, [setVisible, currentPropertyId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: not required
  const handleSubmitButton = useCallback(() => {
    if (addNewAddress) {
      onSubmit();
    } else {
      setNewPropertyId(propertyId);
      setVisible(false);
    }
  }, [setVisible, setNewPropertyId, propertyId, addNewAddress]);

  const onSubmit = handleSubmit(
    async (values) => {
      setLoading(true);

      const presenceNote = meta?.willCustomerBePresent
        ? `${t("pages.serviceDateAndAddress.iWillBeThere")}`
        : meta?.keyInfo || "";

      const newValues = {
        ...values.address,
        floorArea: address?.floorArea || 0,
        notes: `${address?.notes || ""}\n\n${presenceNote}`,
      };

      const { data } = await validateAddress({
        variables: {
          payload: {
            city: address?.city || "",
            localizationId,
            postalOrZipCode: address?.postalOrZipCode || "",
            streetAddress1: address?.streetAddress1 || "",
            streetAddress2: address?.streetAddress2,
          },
        },
      });

      if (!data?.validateAddressInLocalization) {
        setLoading(false);
        return setNotAvailableLocation(true);
      }

      const isSuccess = await createOrUpdateAddress(newValues);

      if (isSuccess) {
        setNewPropertyId(isSuccess?.propertyId || "");

        reset();
        setAddNewAddress(false);
      }
      setLoading(false);
    },
    async (_error) => {
      await trigger();

      if (isWeb) {
        const div = document.querySelectorAll("#form-has-error");
        div?.[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
  );

  useEffect(() => {
    setPropertyId(currentPropertyId);
  }, [currentPropertyId]);

  return (
    <BottomSheet contentStyle={styles.container} toggle={() => handleClose()} visible={visible}>
      <Block alignEnd justifySpace row marginBottom={2}>
        <Typography variant="newH2">
          {addNewAddress ? t("screens.service.newAddress") : t("screens.service.chooseAddress")}
        </Typography>
        <Touchable onPress={() => handleClose()}>
          <Block alignCenter justifyCenter style={styles.closeIcon}>
            <SvgIcon color={palette.common.gray} name="cross" size={36} />
          </Block>
        </Touchable>
      </Block>

      <Divider spaceless />

      <ScrollView>
        {addNewAddress ? (
          <Block justifyCenter marginTop={md ? 3 : 2}>
            <AddressForm
              city={address?.city || ""}
              doorCode={address?.doorCode || ""}
              errors={errors}
              floorArea={address?.floorArea || 0}
              notes={address?.notes || ""}
              postalOrZipCode={address?.postalOrZipCode || ""}
              setValue={setValue}
              trigger={trigger}
              streetAddress1={address?.streetAddress1 || ""}
              streetAddress2={address?.streetAddress2 || ""}
            />

            <HomeTypeQuestion
              setValue={setValue}
              errorMsg={errors?.meta?.homeTypePlaceholder?.message}
              homeType={meta?.homeTypePlaceholder || ""}
            />

            <PresenceInfoQuestion
              errors={errors}
              keyInfo={meta?.keyInfo}
              setValue={setValue}
              willCustomerBePresent={meta?.willCustomerBePresent}
            />
          </Block>
        ) : (
          <>
            {properties.map((property) => (
              <Block key={property.propertyId} marginTop={md ? 3 : 2}>
                <PropertyItem
                  selectItem={() => handleSelectProperty(property.propertyId)}
                  {...property}
                  selected={property?.propertyId === propertyId}
                />
              </Block>
            ))}
            <Block alignStart marginVertical={2}>
              <Button
                disabled={properties.length >= 10}
                label={t("screens.homeEdit.addAddress")}
                onPress={() => setAddNewAddress(true)}
                type="secondary"
                variant="small"
              />
            </Block>
          </>
        )}
      </ScrollView>

      <Divider spaceless />
      <Block marginBottom={6} marginTop={2.5}>
        <Button
          disabled={loading}
          label={t("common.save")}
          loading={loading}
          onPress={() => handleSubmitButton()}
          type="primary"
        />
      </Block>

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />
    </BottomSheet>
  );
};

export default SelectAddressSheet;

const styles = StyleSheet.create({
  closeIcon: {
    backgroundColor: palette.common.black,
    borderRadius: 20,
    borderTopStartRadius: 20,
    marginBottom: spacing.unit * 2.5,
  },
  container: {
    height: spacing.deviceHeight * 0.9,
    padding: spacing.unit * 2.5,
  },
});
